import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  OnInit,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import {
  AppliedFilter,
  BaseFilter,
  FilterCriteria,
  FilterType,
} from '@shared/components/filters/Domain/filter.model';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import { FilterSelectorComponent } from '@shared/components/filters/Components/filter-selector/filter-selector.component';
import { Button } from 'primeng/button';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { DividerModule } from 'primeng/divider';
import { SharedModule } from '@shared/shared.module';
import { FilterService } from '@shared/components/filters/Application/Services/filter.service';
import { isFullDayRange, isTodayRange } from '@shared/helpers/date-utils';
import { FilterStorageService } from '@shared/components/filters/Application/Services/filter-storage.service';
import { TimezoneService } from '@shared/services/timezone.service';
import { SearchOrigin } from '@shared/components/filters/Domain/search-method.enum';
import { FiltersUtilsService } from '@shared/components/filters/Application/Services/filters-utils.service';
import { ComposedFilterDisplayComponent } from '@shared/components/filters/Components/filter-container/composed-filter-display/composed-filter-display.component';

@Component({
  selector: 'app-filter-container',
  standalone: true,
  imports: [
    NgSwitchCase,
    NgIf,
    NgSwitch,
    NgForOf,
    FilterSelectorComponent,
    NgSwitchDefault,
    Button,
    OverlayPanelModule,
    DividerModule,
    SharedModule,
    ComposedFilterDisplayComponent,
  ],
  templateUrl: './filter-container.component.html',
  styleUrl: './filter-container.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FilterContainerComponent implements OnInit, OnDestroy {
  @ViewChild('opFilter') opFilter!: OverlayPanel;
  @Input() filters: BaseFilter[] = [];
  @Input() defaultFilterApplied: AppliedFilter[] = [];
  @Output() appliedFilters = new EventEmitter<{ origin: SearchOrigin; filters: FilterCriteria }>();

  resetTrigger = false;
  showFilterSelector = false;
  selectedFilters: AppliedFilter[] = [];
  removedFilterId: string | null = null;
  filtersChanged = false;

  protected readonly FilterType = FilterType;
  protected readonly Array = Array;

  constructor(
    public filterService: FilterService,
    private filterStorage: FilterStorageService,
    private timezoneService: TimezoneService,
    private filtersUtils: FiltersUtilsService,
  ) {}

  ngOnInit(): void {
    if (Object.keys(this.defaultFilterApplied).length > 0) {
      this.onAppliedFiltersChange(this.defaultFilterApplied, false);

      this.filterService.setDefaultFilters(this.defaultFilterApplied);
    }
  }

  itCloseOp(close: boolean): void {
    this.showFilterSelector = close;
  }

  onAppliedFiltersChange(filters: AppliedFilter[], filtersChanged: boolean = true): void {
    const normalizedFilters = filters.map((f) => {
      const valueIsEmpty = !f.value || (Array.isArray(f.value) && f.value.length === 0);
      return {
        ...f,
        isApplied: valueIsEmpty ? false : f.isApplied,
      };
    });

    if (normalizedFilters.length > 0) {
      this.resetTrigger = false;
    }

    const cleanedFilters = this.filterService.sanitizeAppliedFilters(
      normalizedFilters,
      this.filters,
    );

    //Condition to check if filters are valid on different parent components (could have same ids for diff pages)
    if (this.filterService.areAppliedFiltersValid(cleanedFilters, this.filters)) {
      this.selectedFilters = normalizedFilters.filter((filter) => filter.isApplied);
      this.filterService.setFilters(this.selectedFilters);
    } else {
      this.filtersChanged = false;
      this.reset();
    }
  }

  removeAppliedFilter(filterId: string): void {
    this.selectedFilters = this.selectedFilters.filter((filter) => filter.filter.id !== filterId);
    this.removedFilterId = filterId;
    this.filtersChanged = true;
    this.filterService.removeFilter(filterId);
    const sanitizedFilters = this.selectedFilters.map((f) => ({
      ...f,
      value: this.sanitizeValue(f.value),
    }));

    if (filterId == 'workflows') {
      this.filterStorage.clear('composedFilter');
    }

    this.appliedFilters.emit({
      origin: SearchOrigin.FILTER_CHANGE,
      filters: this.filtersUtils.mapFiltersToSearchCriteria(sanitizedFilters),
    });
  }

  search(): void {
    this.itCloseOp(true);
    this.opFilter.hide();
    this.filtersChanged = false;
    this.emitFilters();
  }

  private emitFilters(): void {
    const sanitizedFilters = this.selectedFilters.map((f) => ({
      ...f,
      value: this.sanitizeValue(f.value),
    }));

    this.appliedFilters.emit({
      origin: SearchOrigin.FILTER_CHANGE,
      filters: this.filtersUtils.mapFiltersToSearchCriteria(sanitizedFilters),
    });
  }

  private sanitizeValue(value: any): any {
    if (Array.isArray(value)) {
      return value.map((v) => (typeof v === 'string' ? v.replace(/[\t\n\r]/g, '').trim() : v));
    }
    return typeof value === 'string' ? value.replace(/[\t\n\r]/g, '').trim() : value;
  }

  reset(): void {
    this.resetTrigger = !this.resetTrigger;
    this.selectedFilters = [];
    this.filtersChanged = true;
    this.filterService.resetDefaultFilters();
    this.filterService.resetFilters();
    this.appliedFilters.emit({
      origin: SearchOrigin.RESET_OR_RELOAD,
      filters: [],
    });
    //Erase composed filter from storage should be improved, problems with circular references of JSON
    this.filterStorage.clear('composedFilter');
  }

  getOptionCount(filter: BaseFilter, value: any): number | undefined {
    return this.filterService.getOptionCount(filter, value);
  }

  getOptionLabels(appliedFilter: AppliedFilter): string {
    return this.filterService.getOptionLabels(appliedFilter);
  }
  getFilteredItems(value: any, maxItems: number = 2): { displayed: string[]; moreCount: number } {
    return this.filterService.getFilteredItems(value);
  }

  getRadioButtonLabel(appliedFilter: AppliedFilter, maxItems: number = 2) {
    return this.filterService.getRadioButtonLabel(appliedFilter);
  }

  truncateLongWords(text: string, maxLength: number = 10): string {
    return text
      .split(' ')
      .map((word) => (word.length > maxLength ? word.slice(0, maxLength) + '...' : word))
      .join(' ');
  }

  isTodayFullRange(start: string, finish: string): boolean {
    return isTodayRange(start, finish, this.timezoneService);
  }

  protected readonly isFullDayRange = isFullDayRange;

  ngOnDestroy(): void {
    this.filterService.resetFilters();
  }
}
